import { Activity, Bell, History, LayoutDashboard, LogOut, MessageSquare, MoreVertical, PanelLeftClose, PanelLeftOpen, Plus, RefreshCcw, Settings, Share2, ShieldCheck, Star, Users, BarChart3 } from "lucide-react";
import { useState } from "react";
import { Navigate, NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import Button from "../components/ui/Button";
import { useAuth } from "../providers/AuthProvider";
import pkg from "../../package.json";

export default () => {
  const { session, isLoading, logout: logoutAuth } = useAuth();
  const [isSidebarOpen, setSidebarOpen] = useState(false);
  const [isCollapsed, setCollapsed] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const displayName = session?.name ?? "";
  const displaySubtitle = session ? session.email : "";
  const displayInitial = displayName.charAt(0).toUpperCase();

  const handleLogout = async () => {
    await logoutAuth();
    navigate("/");
  };

  const path = location.pathname;
  const activePath = path === "/dashboard" ? "/dashboard/chat" : path;
  const section = activePath.replace("/dashboard", "") || "/";
  const pageTitle = section === "/" ? "Chat" : section.replace("/", "").split("-").map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(" ");
  const isChat = activePath === "/dashboard/chat" || activePath === "/dashboard";

  if (isLoading) {
    return <div className="flex min-h-screen items-center justify-center bg-slate-50 text-sm font-medium text-slate-500">Loading...</div>;
  }

  if (!session) {
    return <Navigate to="/" replace />;
  }

  if (!session.onboardingCompleted) {
    return <Navigate to="/signup" replace />;
  }

  const navItems = [
    { href: "/dashboard/chat", label: "Chat", icon: MessageSquare, roles: ["team", "admin"] },
    { href: "/dashboard/reports", label: "Reports", icon: BarChart3, roles: ["team", "admin"] },
    { href: "/dashboard/integrations", label: "Integrations", icon: Share2, roles: ["admin"] },
    { href: "/dashboard/automations", label: "Automations", icon: Bell, roles: ["team", "admin"] },
    { href: "/dashboard/favorites", label: "Favorites", icon: Star, roles: ["team", "admin"] },
    { href: "/dashboard/history", label: "History", icon: History, roles: ["team", "admin"] },
    { href: "/dashboard/team", label: "Team", icon: Users, roles: ["admin"] },
    { href: "/dashboard/prompt-settings", label: "Settings", icon: Settings, roles: ["team", "admin"] },
  ].filter(item => item.roles.includes(session.role));

  return (
    <div className="flex min-h-screen bg-slate-50 text-slate-900 selection:bg-indigo-100">
      {isSidebarOpen && <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm lg:hidden" onClick={() => setSidebarOpen(false)} />}

      <aside className={`fixed inset-y-0 left-0 z-50 border-r border-white/5 bg-[var(--theme-sidebar-bg)] transition-all duration-300 ${isCollapsed ? "w-[72px]" : "w-64 md:w-72"} lg:translate-x-0 ${isSidebarOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}`}>
        <div className="flex h-full flex-col p-4">
          <div className={`mb-8 flex flex-col items-center gap-2 ${isCollapsed ? "" : "px-2"}`}>
            <div className={`flex w-full items-center ${isCollapsed ? "justify-center" : "justify-between"}`}>
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-indigo-600 shadow-sm"><Activity className="h-5 w-5 text-white" /></div>
                {!isCollapsed && <span className="text-xl font-semibold tracking-tight text-white">Quantira</span>}
              </div>
              {!isCollapsed && (
                <div className="flex items-center gap-1">
                  <button onClick={() => setCollapsed(prev => !prev)} className="hidden rounded-lg p-2 text-slate-600 transition-colors hover:bg-white/5 hover:text-slate-300 lg:flex">
                    <PanelLeftClose className="h-4 w-4" />
                  </button>
                  <button className="p-2 lg:hidden" onClick={() => setSidebarOpen(false)}><Plus className="h-5 w-5 rotate-45 text-slate-500" /></button>
                </div>
              )}
            </div>
            {isCollapsed && (
              <button onClick={() => setCollapsed(false)} className="hidden rounded-lg p-2 text-slate-600 transition-colors hover:bg-white/5 hover:text-slate-300 lg:flex">
                <PanelLeftOpen className="h-4 w-4" />
              </button>
            )}
          </div>

          <nav className="no-scrollbar flex-1 space-y-1 overflow-y-auto">
            {navItems.map(item => (
              <NavLink key={item.href} to={item.href} title={isCollapsed ? item.label : undefined} className={`group flex items-center gap-3 rounded-xl ${isCollapsed ? "justify-center px-2" : "px-4"} py-3 text-sm font-bold tracking-wide transition-all ${activePath === item.href ? "bg-indigo-500/20 text-indigo-400 ring-1 ring-indigo-500/20 shadow-sm" : "text-slate-500 hover:bg-white/5 hover:text-slate-200"}`}>
                <item.icon className={`h-4.5 w-4.5 shrink-0 transition-colors ${activePath === item.href ? "text-indigo-400" : "text-slate-600 group-hover:text-slate-300"}`} />
                {!isCollapsed && item.label}
              </NavLink>
            ))}

            {session.role === "admin" && (
              <div className="mt-4 border-t border-slate-800/50 pt-4">
                <NavLink to="/admin" title={isCollapsed ? "Admin Console" : undefined} className={`flex items-center gap-3 rounded-xl ${isCollapsed ? "justify-center px-2" : "px-4"} py-3 text-sm font-bold text-blue-400 transition-all hover:bg-blue-500/5`}><ShieldCheck className="h-4.5 w-4.5 shrink-0" />{!isCollapsed && "Admin Console"}</NavLink>
              </div>
            )}
          </nav>

          <div className="mt-auto space-y-3 pt-4">
            {isCollapsed ? (
              <div className="flex flex-col items-center gap-2">
                <div className="flex h-9 w-9 items-center justify-center rounded-lg border border-slate-700 bg-slate-800 text-xs font-bold text-slate-300">{displayInitial}</div>
              </div>
            ) : (
              <div className="rounded-2xl border border-slate-800 bg-slate-900/50 p-4 shadow-inner ring-1 ring-white/5">
                <div className="flex items-center gap-3">
                  <div className="flex h-9 w-9 items-center justify-center rounded-lg border border-slate-700 bg-slate-800 text-xs font-bold text-slate-300">{displayInitial}</div>
                  <div className="flex-1 overflow-hidden">
                    <p className="truncate text-sm font-bold text-white">{displayName}</p>
                    <p className="truncate text-xs font-semibold text-slate-500">{displaySubtitle}</p>
                  </div>
                </div>
                <div className="mt-4 flex gap-2">
                  <Button variant="ghost" size="sm" className="flex-1 text-xs font-bold text-rose-500 hover:bg-rose-500/10" onClick={() => void handleLogout()}><LogOut className="mr-1.5 h-3 w-3" /> Logout</Button>
                </div>
              </div>
            )}
            <p className={`text-[10px] font-medium text-slate-600 ${isCollapsed ? "text-center" : "px-2"}`}>v{pkg.version}</p>
          </div>
        </div>
      </aside>

      <main className={`flex-1 transition-all duration-300 ${isCollapsed ? "lg:pl-[72px]" : "lg:pl-72"}`}>
        {isChat ? (
          <div className="h-screen"><Outlet /></div>
        ) : (
          <div className="mx-auto max-w-[1400px] p-4 md:p-10">
            <header className="mb-6 flex items-center justify-between md:mb-10">
              <div className="flex items-center gap-4">
                <button className="rounded-lg border border-slate-200 bg-white p-2 lg:hidden" onClick={() => setSidebarOpen(true)}><MoreVertical className="h-5 w-5 text-slate-600" /></button>
                <div>
                  <div className="hidden items-center gap-2 text-xs font-semibold text-slate-400 md:flex"><LayoutDashboard className="h-3 w-3" /> System Control / {section.replace("/", "").replace("-", " ") || "Overview"}</div>
                  <h1 className="text-2xl font-semibold tracking-tight text-slate-900 md:mt-1 md:text-4xl">{pageTitle}</h1>
                </div>
              </div>
            </header>

            <div className="animate-in slide-in-from-bottom-4 fade-in duration-700"><Outlet /></div>
          </div>
        )}
      </main>
    </div>
  );
};
